import { AfterViewInit, Component, ElementRef, OnDestroy, OnInit } from '@angular/core';
import { ModalController } from '@ionic/angular';
import { TranslateService } from '@ngx-translate/core';
import { PictogramModalComponent } from 'src/app/components/pictogram-modal/pictogram-modal.component';
import { BackendCallService } from 'src/app/services/backend-call/backend-call.service';
import { ConfirmationService } from 'src/app/services/confirmation.service';
import { ToastService } from 'src/app/services/toast.service';
import { UserAuthService } from 'src/app/services/user-auth/user-auth.service';
import { AppConstants } from 'src/app/utilities/constant';
import { LoaderService } from 'src/app/services/loader/loader.service';

@Component({
  selector: 'app-library-pictogram-library',
  templateUrl: './library-pictogram-library.component.html',
  styleUrls: ['./library-pictogram-library.component.scss'],
})
export class LibraryPictogramLibraryComponent implements OnInit, AfterViewInit, OnDestroy {
  public pictogramcategoriesData: any;
  public isPictogramCategoriesLoaded = false;
  private toggleIconObserver?: MutationObserver;

  constructor(
    private confirmationService: ConfirmationService,
    private modalController: ModalController,
    public backendService: BackendCallService,
    private toastService: ToastService,
    public translate: TranslateService,
    public userAuthService: UserAuthService,
    private loaderservice: LoaderService,
    private hostElement: ElementRef<HTMLElement>,
  ) {}

  /**
   * Lifecycle hook that initializes the component by fetching categories data.
   */
  ngOnInit() {
    this.loaderservice.show();
    this.getCategoriesData();
  }

  ngAfterViewInit() {
    this.setupAccordionToggleFocus();
  }

  ngOnDestroy(): void {
    this.toggleIconObserver?.disconnect();
  }

  // ─── INLINE EDIT: Enable editing on filename click ────────────
  // Store original name when user focuses

  /**
   * Handles the focus event on the image name input for inline editing.
   * Initializes displayName and stores the original name.
   */
  public onNameFocus(img: any) {
    if (!img.displayName) {
      img.displayName = img.fileName;
    }
    img.originalName = img.displayName.trim();
    img.isDirty = false;
  }


  private setupAccordionToggleFocus(): void {
    // Bind any icons already present (e.g. on page revisit)
    this.bindNewToggleIcons();

    // MutationObserver fires immediately whenever Ionic injects a new icon,
    // covering initial load, *ngFor re-renders and any data refreshes.
    this.toggleIconObserver?.disconnect();
    this.toggleIconObserver = new MutationObserver(() => {
      this.bindNewToggleIcons();
    });
    this.toggleIconObserver.observe(this.hostElement.nativeElement, {
      childList: true,
      subtree: true,
    });
  }

  private bindNewToggleIcons(): void {
    // Only select icons not yet bound (avoids duplicate listeners)
    const icons = this.hostElement.nativeElement.querySelectorAll(
      '.ion-accordion-toggle-icon:not([data-focus-bound])',
    );
    icons.forEach((iconEl) => {
      const icon = iconEl as HTMLElement;
      icon.tabIndex = 0;
      icon.setAttribute('role', 'button');
      icon.setAttribute('aria-label', this.translate.instant('Toggle'));
      icon.dataset.focusBound = 'true';

      icon.addEventListener('keydown', (keyEvent: KeyboardEvent) => {
        if (keyEvent.key !== 'Enter' && keyEvent.key !== ' ') {
          return;
        }
        keyEvent.preventDefault();
        keyEvent.stopPropagation();
        const accordion = icon.closest('ion-accordion') as HTMLElement | null;
        const headerItem = accordion?.querySelector(
          'ion-item[slot="header"]',
        ) as HTMLElement | null;
        headerItem?.click();
      });
    });
  }

  /**
   * Handles the blur event on the image name input for inline editing.
   * Triggers the update process.
   */
  public onNameBlur(img: any, category: any) {
    this.triggerUpdate(img, category);
  }

  // mouseleave on image-card — fires when cursor moves away

  /**
   * Handles the mouse leave event on the image card for inline editing.
   * Triggers the update process.
   */
  public onImageCardMouseLeave(img: any, category: any) {
    this.triggerUpdate(img, category);
  }

  // Single method that handles validation + API call

  /**
   * Triggers the update process for inline editing of image names.
   * Validates the new name and calls the API if valid.
   */
  private triggerUpdate(img: any, category: any) {
    const newName = img.displayName?.trim() || '';

    if (!img.originalName) return;

    // No-op when user didn't actually change the name.
    if (newName === img.originalName) {
      return;
    }

    if (newName.length < 1) {
      img.displayName = img.originalName;
      this.toastService.presentToast(
        this.translate.instant('new_name_should_be_at_least_one_character'),
        3000,
        AppConstants.WARNING_SNACKBAR,
        'top',
      );
      return;
    }

    if (this.isDuplicateNameInCategory(category, img, newName)) {
      img.displayName = img.originalName;
      this.toastService.presentToast(
        this.translate.instant('image_name_already_exists_in_this_category'),
        3000,
        AppConstants.WARNING_SNACKBAR,
        'top',
      );
      return;
    }

    if (newName !== img.originalName) {
      img.originalName = newName;
      this.updateImageName(img, newName);
    }
  }

  /**
   * Checks whether a given image name already exists in the current category.
   */
  private isDuplicateNameInCategory(
    category: any,
    currentImg: any,
    candidateName: string,
  ): boolean {
    const normalizedCandidate = this.normalizeImageName(candidateName);

    if (!normalizedCandidate || !category?.images?.length) {
      return false;
    }

    return category.images.some((img: any) => {
      if (img?.id === currentImg?.id) {
        return false;
      }

      const existingName = this.normalizeImageName(
        img?.displayName || img?.fileName || '',
      );

      return existingName === normalizedCandidate;
    });
  }

  /**
   * Normalizes image name for case-insensitive duplicate matching.
   */
  private normalizeImageName(value: string): string {
    return value.trim().toLowerCase();
  }

  // ─── INLINE EDIT: API call to update image name ───────────────

  /**
   * Makes an API call to update the image name on the server.
   */
  private updateImageName(img: any, newName: string) {
    const payload = {
      id: img.id,
      fileName: newName,
      modifiedBy: this.userAuthService.getUserList().userId,
    };

    this.backendService.renamePictogramImages(payload).subscribe(
      (res) => {
        if (res?.flag) {
          this.toastService.presentToast(
            this.translate.instant('image_name_updated_successfully'),
            3000,
            AppConstants.SUCCESS_SNACKBAR,
            'top',
          );
          this.getCategoriesData(); // refresh all data
        }
      },
      (err) => {
        this.toastService.presentToast(
          err.error?.message,
          3000,
          AppConstants.ERROR_SNACKBAR,
          'top',
        );
        // Revert on API failure
        img.displayName = img.originalName;
      },
    );
  }

  /**
   * Makes an API call to add  images  to selected  category
   * */
  public async addpictogramimages() {
    const modal = await this.modalController.create({
      component: PictogramModalComponent,
      componentProps: {
        mode: 'images',
        categories: this.pictogramcategoriesData,
      },
      cssClass: 'pictogram-image-modal',
      backdropDismiss: true,
    });

    await modal.present();
    const { data } = await modal.onDidDismiss();

    if (data?.ImageData) {
      this.backendService.UploadPictogramImages(data?.ImageData).subscribe(
        (res) => {
          if (res?.flag) {
            this.toastService.presentToast(
              this.translate.instant(data.ImageData.length > 1 ? 'pictogram_images_added_successfully' : 'pictogram_image_added_successfully'),
              3000,
              AppConstants.SUCCESS_SNACKBAR,
              'top',
            );
            this.getCategoriesData();
          }
        },
        (err) => {
          this.toastService.presentToast(
            err.error?.message,
            3000,
            AppConstants.ERROR_SNACKBAR,
            'top',
          );
        },
      );
    }
  }

  /**

 * Opens the pictogram modal in edit mode to update a category.

 */

  public async onEditCategory(category: any, event: Event) {
    event.stopPropagation();
    event.preventDefault();

    const modal = await this.modalController.create({
      component: PictogramModalComponent,
      cssClass: 'pictogram-category-modal',
      componentProps: {
        isEditMode: true,
        categoryData: category,
      },
    });

    modal.onDidDismiss().then((result) => {
      if (result.data?.categoryName && result.data?.id) {
        let payload = {
          id: result?.data?.id,
          categoryName: result?.data?.categoryName,
        };
        this.backendService.EditPictogramCategory(payload).subscribe(
          (res) => {
            if (res?.flag) {
              this.toastService.presentToast(
                this.translate.instant('category_updated_successfully'),
                3000,
                AppConstants.SUCCESS_SNACKBAR,
                'top',
              );
              this.getCategoriesData();
            }
          },
          (err) => {
            this.toastService.presentToast(
              err.error?.message,
              3000,
              AppConstants.ERROR_SNACKBAR,
              'top',
            );
          },
        );
      }
    });

    await modal.present();
  }

  /**

 * Deletes a category after confirmation.

 */

  public async onDeleteCategory(category: any, event: Event) {
   event.stopPropagation();
    event.preventDefault();
     const item = this.translate.instant('category').toLowerCase();
    const itemName = this.translate.instant(category.name);
    const title =
      this.translate.instant('Are_you_sure_you_want_to_delete') +
      ' ' +
      item +
      '?';
    const subtitle = `'${category.name}'`;
    const actionText = this.translate.instant('Delete');
    const isConfirmed = await this.confirmationService.presentConfirmationModal(
      title,
      actionText,
      true,
      this.translate.instant('Cancel'),
      subtitle,
    );
    if (isConfirmed) {
      const categoryId = category.id;
      let payload = { categoryId: categoryId };
      this.backendService.DeletePictogramCategory(payload).subscribe(
        (res) => {
          if (res) {
            this.toastService.presentToast(
              this.translate.instant('category_deleted_successfully'),
              3000,
              AppConstants.SUCCESS_SNACKBAR,
              'top',
            );
            this.getCategoriesData();
          }
        },
        (err) => {
          const backendMessage = err?.error?.message;
          const errorMessage = typeof backendMessage === 'string' && /cannot delete category|images? associated/i.test(backendMessage)
            ? 'Cannot delete category. It has images associated with it. Please delete the images first.'
            : backendMessage;

          this.toastService.presentToast(
            errorMessage,
            3000,
            AppConstants.ERROR_SNACKBAR,
            'top',
          );
        },
      );
    }
  }

  /**

 * Deletes a category image after confirmation.

 */

  public async onDeleteCategoryImage(image: any) {
    const item = this.translate.instant('Image').toLowerCase();
    const title =
      this.translate.instant('Are_you_sure_you_want_to_delete') +
      ' ' +
      item +
      '?';
    const subtitle = `'${image.fileName}'`;
    const actionText = this.translate.instant('Delete');
    const isConfirmed = await this.confirmationService.presentConfirmationModal(
      title,
      actionText,
      true,
      this.translate.instant('Cancel'),
      subtitle,
    );
    if (isConfirmed) {
      const imageId = image.id;
      let payload = { imageId: imageId };
      this.backendService.DeletePictogramCategoryImage(payload).subscribe(
        (res) => {
          if (res) {
            this.toastService.presentToast(
              image.fileName +
                ' ' +
                this.translate.instant('image_deleted_successfully'),
              3000,
              AppConstants.SUCCESS_SNACKBAR,
              'top',
            );
            this.getCategoriesData();
          }
        },
        (err) => {
          const backendMessage = err?.error?.message;
          const errorMessage = typeof backendMessage === 'string' && /pictogram.*cannot.*delet|cannot.*delet.*pictogram|linked.*hazard/i.test(backendMessage)
            ? this.translate.instant('pictogram_image_linked_to_hazard')
            : backendMessage;
          this.toastService.presentToast(
            errorMessage,
            3000,
            AppConstants.ERROR_SNACKBAR,
            'top',
          );
        },
      );
    }
  }

  /**

 * Opens the pictogram modal in 'category' mode to add a new category.

 */
  public async addcategory() {
    const modal = await this.modalController.create({
      component: PictogramModalComponent,
      componentProps: { mode: 'category' },
      cssClass: 'pictogram-category-modal',
      backdropDismiss: true,
    });

    await modal.present();
    const { data } = await modal.onDidDismiss();

    if (data?.categoryName) {
      let payload = {
        categoryName: data.categoryName,
      };
      this.backendService.CreatePictogramCategory(payload).subscribe(
        (res) => {
          if (res?.flag) {
            this.toastService.presentToast(
              this.translate.instant('category_added_successfully'),
              3000,
              AppConstants.SUCCESS_SNACKBAR,
              'top',
            );
            this.getCategoriesData();
          }
        },
        (err) => {
          this.toastService.presentToast(
            err.error?.message,
            3000,
            AppConstants.ERROR_SNACKBAR,
            'top',
          );
        },
      );
    }
  }

  /**

 * Fetches pictogram categories data from the backend.

 */

  private getCategoriesData() {
    this.isPictogramCategoriesLoaded = false;
    this.backendService.getPictogramCategories().subscribe(
      (res: any) => {
        this.pictogramcategoriesData = res?.response || [];
        this.isPictogramCategoriesLoaded = true;
        this.loaderservice.hide();
      },
      (err) => {
        this.isPictogramCategoriesLoaded = true;
        this.loaderservice.hide();
        this.toastService.presentToast(
          err.error?.message,
          3000,
          AppConstants.ERROR_SNACKBAR,
          'top',
        );
      },
    );
  }
}
